/**
 * สไลด์ (the ground slide, Ctrl + a direction; systems/slide.ts): a low wuxia skid along the ground, out of a run or
 * from standing. It sets off at once and comes to rest under a steady friction (the speed falls in a straight line,
 * so the distance eases out: d(s) = dist · (1 − (1 − s)²), s = time / `time`). Research: docs/research/MOTION_SMOOTH.md.
 */
export const SLIDE = {
  /** Metres covered. */
  dist: 4,
  /** Seconds from setting off to rest (the speed starts at 2 · dist / time, about 11 m/s). */
  time: 0.7,
  /** Stamina (พลังกาย) it costs. */
  stamina: 15,
  /** From this share of the slide on, a move or a jump cuts it short (the end window). */
  cancelAt: 0.6,
  /** Seconds after a slide before the next. */
  cooldown: 0.35,
  /** A step that moved less than this share of what it should (a wall in the way) ends the slide. */
  blocked: 0.25,
} as const;

/** Metres along the slide at progress `s` (0–1), for a slide of `len` metres. */
export const slideDistAt = (s: number, len: number = SLIDE.dist): number => {
  const k = s <= 0 ? 0 : s >= 1 ? 1 : s;
  return len * (1 - (1 - k) * (1 - k));
};

/**
 * สไลด์ in a duel bout (`DUEL_FLAGS.v2`, systems/duelSlide.ts; the open world keeps `SLIDE`). The ground reposition: longer and
 * quicker than a step, no i-frames, but low (air attacks pass over it), a long stop when nobody cuts it short, and a slide strike
 * out of its end window. Research: docs/research/DUEL_SLIDE.md. Starting numbers; ns-balance tunes.
 */
export const DUEL_SLIDE = {
  /** Metres covered (a step covers 2.2-4) and seconds to rest (about 18 m/s at the start). */
  dist: 4.5,
  time: 0.5,
  /** พลังกาย it costs; a second slide inside `spamWindow` costs x`spamMult`. */
  stamina: 20,
  spamWindow: 1.5,
  spamMult: 1.5,
  /** From this share of the slide on, a strike, a jump or a step cuts it short (the end window). */
  cancelAt: 0.55,
  /** Toward the foe it stops this far short of them (no sliding through); the foe is "in the way" within `lane` of the path. */
  short: 0.9,
  lane: 0.9,
  /** Low: for this share of the slide, a blow from a fighter in the air (a dive, an air move) passes over. */
  low: 0.75,
  /** The stop after a slide that ran out (nobody cut it short): longer than every step's, the punish for a misread. */
  stop: 0.4,
  /** Slide strike (the basic cut or an attack move started in the end window; pressed earlier, it waits for the window). */
  strike: { power: 1.2, guardedStop: 0.35, hold: 0.6 },
} as const;
